/**
 * Helper: Product variants
 * -----------------------------------------------------------------------------
 * Lazily fetches variant pages beyond a product's initial `variants(first: N)`
 * fetch (see `formatProduct()`'s `variantsIncomplete`/`variantsEndCursor`).
 * - Used by surfaces that only ever receive an already-*formatted* product
 *   (Quick Add, Bought Together) rather than the raw GraphQL response
 *   `main-product.vue`'s own `loadRemainingVariants()` mutates in place -
 *   those surfaces render off a product prop passed down from a collection/
 *   card query, so there's no raw response left to mutate by the time the
 *   overlay opens.
 *
 */
import productVariantsQuery from '~graphql/product-variants.gql';
import { formatVariants } from '~/format/format-variant';

/**
 * Fetch and format every remaining variant page for a product, appending
 * them to a shallow copy of its `variants` array.
 * - No-op (returns the same product reference) when the product's initial
 *   fetch already contained every variant.
 * @param {Object} product - Formatted product (`formatProduct()` output).
 * @param {Object} [data] - Additional data.
 * @param {Object} [data.collection] - Formatted collection object, passed
 * through to `formatVariants()` for collection-specific pricing/badges.
 * @param {Function} [data.isStale] - Called before each page is merged in -
 * return `true` to abandon the fetch (e.g. the calling component has since
 * been given a different product) without touching the input product.
 * @returns {Promise<Object>} The same product when nothing needed loading,
 * otherwise a new product object with `variants`/`variantsIncomplete`/
 * `variantsEndCursor` updated.
 */
export async function loadRemainingFormattedVariants(
  product,
  { collection, isStale = () => false } = {},
) {
  if (!product?.variantsIncomplete) {
    return product;
  }

  const variants = [...product.variants];
  let hasNextPage = true;
  let endCursor = product.variantsEndCursor;

  while (hasNextPage) {
    if (isStale()) {
      return product;
    }

    let response;

    try {
      // eslint-disable-next-line no-await-in-loop
      response = await cnvs.Query({
        query: productVariantsQuery,
        variables: {
          after: endCursor,
          country: cnvs.store.country,
          handle: product.handle,
          language: cnvs.store.language,
        },
      });
    } catch (error) {
      cnvs.ReportError('Failed to load remaining product variants', error);
      break;
    }

    if (isStale()) {
      return product;
    }

    const nextVariants = response?.product?.variants;

    if (!nextVariants) {
      break;
    }

    // `formatVariant()` only reads `handle` (for `variant.url`) and
    // `trackingParameters` (not available post-format, so left undefined -
    // `variant.url` isn't the link surfaced by Quick Add/Bought Together,
    // which use the already-tracked `product.url` instead).
    variants.push(
      ...formatVariants(
        { handle: product.handle, variants: nextVariants },
        collection,
      ),
    );

    hasNextPage = Boolean(nextVariants.pageInfo?.hasNextPage);
    endCursor = nextVariants.pageInfo?.endCursor ?? null;
  }

  return {
    ...product,
    variants,
    variantsIncomplete: hasNextPage,
    variantsEndCursor: endCursor,
  };
}
